import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { WarehouseAPI } from '../services/api';
import { usePermission } from '../hooks/usePermission';
import DebundleWorkflow from '../components/debundle/DebundleWorkflow';

const Debundle: React.FC = () => {
    // RBAC: the entire page is a bundle-reduction (reduce-bundle) mutation flow. A view-only
    // caller gets a read-only notice instead of the workflow; the backend independently
    // rejects any write with 403 (defense in depth, SI-5).
    const canEdit = usePermission('reduce-bundle').can('edit');
    const [loading, setLoading] = useState(true);
    const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        WarehouseAPI.getUnicommerceStatus()
            .then((data) => {
                if (!mounted) return;
                if (data?.isRunning) {
                    const msg = data.serverMessage || 'Inventory Syncing in progress';
                    setBlockedMessage(msg);
                } else {
                    setBlockedMessage(null);
                }
            })
            .catch(() => {
                if (!mounted) return;
                setBlockedMessage(null);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canEdit) {
        return (
            <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6 }}>
                <Alert severity="info">
                    <Typography variant="h6">View-only access</Typography>
                    <Typography variant="body2">You don&apos;t have edit access to reduce bundles. Contact an admin if you need it.</Typography>
                </Alert>
            </Box>
        );
    }

    if (blockedMessage) {
        return (
            <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6 }}>
                <Alert severity="info">
                    <Typography variant="h6">{blockedMessage}</Typography>
                    <Typography variant="body2">Please try again after the inventory sync completes.</Typography>
                </Alert>
            </Box>
        );
    }

    return <DebundleWorkflow />;
};

export default Debundle;
